/**
 * Campaign attribution capture.
 *
 * Ad URLs (LinkedIn, Google, newsletters) carry `utm_*` query parameters, but
 * our visit tracker only records `location.pathname`, so those parameters used
 * to be discarded the moment the router took over. This module grabs them on
 * first landing and keeps them for the rest of the browser session, so every
 * later pageview and the careers apply events can be attributed back to the
 * campaign that produced the visit.
 *
 * Session-scoped on purpose: a campaign click owns the session it started, and
 * a later organic visit should not inherit that campaign.
 */

export type UtmParams = {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
};

const KEY = "axl_utm";
const EMPTY: UtmParams = {
  utmSource: "",
  utmMedium: "",
  utmCampaign: "",
  utmContent: "",
  utmTerm: "",
};

function clean(value: string | null): string {
  return (value ?? "").trim().slice(0, 160);
}

function hasAny(u: UtmParams): boolean {
  return Boolean(u.utmSource || u.utmMedium || u.utmCampaign || u.utmContent || u.utmTerm);
}

/**
 * Reads `utm_*` from the current URL and, when present, makes them the session's
 * attribution. Falls back to whatever was stored earlier in the session. Also
 * infers a coarse source from LinkedIn's own click IDs so app-referred clicks
 * without full tagging still land somewhere useful.
 */
export function getUtmParams(): UtmParams {
  if (typeof window === "undefined") return EMPTY;

  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl: UtmParams = {
      utmSource: clean(params.get("utm_source")),
      utmMedium: clean(params.get("utm_medium")),
      utmCampaign: clean(params.get("utm_campaign")),
      utmContent: clean(params.get("utm_content")),
      utmTerm: clean(params.get("utm_term")),
    };

    // LinkedIn Campaign Manager appends its own `hsa_*` parameters to every ad
    // URL. All creatives in an ad set share one `utm_campaign`, so without
    // these the five variants collapse into a single indistinguishable row.
    // `hsa_ad` is the creative id and `hsa_grp` the ad set id, so we park them
    // in content/term whenever the URL did not tag those slots itself.
    if (!fromUrl.utmContent) fromUrl.utmContent = clean(params.get("hsa_ad"));
    if (!fromUrl.utmTerm) fromUrl.utmTerm = clean(params.get("hsa_grp"));

    // LinkedIn appends `li_fat_id` on ad clicks even when a link is untagged.
    if (!hasAny(fromUrl) && params.get("li_fat_id")) {
      fromUrl.utmSource = "linkedin";
      fromUrl.utmMedium = "paid_social";
    }

    if (hasAny(fromUrl)) {
      window.sessionStorage.setItem(KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }


    const stored = window.sessionStorage.getItem(KEY);
    if (stored) return { ...EMPTY, ...(JSON.parse(stored) as Partial<UtmParams>) };
  } catch {
    /* private mode, blocked storage, malformed JSON: attribution is optional */
  }

  return EMPTY;
}
